import Choices from "choices.js";

const buildAsyncPicker = ({
    model,
    initial,
    options = [],
    optionLabelKey = "label",
    async: asyncConfig = null,
    onOptionSelect = null,
}) => ({
    value: null,
    instance: null,
    optionLabelKey,
    optionsByValue: new Map(),
    asyncConfig,
    onOptionSelect,
    searchDebounceTimer: null,
    searchAbortController: null,
    init() {
        this.value = model ? this.$wire.entangle(model).live : (initial ?? null);

        const shouldSearchRemotely = Boolean(this.asyncConfig?.endpoint);

        this.instance = new Choices(this.$refs.select, {
            searchChoices: !shouldSearchRemotely,
            shouldSort: false,
            allowHTML: false,
        });

        this.captureInitialOptions(options);

        if (
            this.value !== undefined &&
            this.value !== null &&
            String(this.value) !== ""
        ) {
            this.instance.setChoiceByValue(String(this.value));
        }

        this.instance.passedElement.element.addEventListener(
            "addItem",
            (event) => {
                this.handleSelection(
                    event.detail.value,
                    event.detail.customProperties?.option ?? null,
                );
            },
        );

        this.$refs.select.addEventListener("change", () => {
            const currentValue = this.instance.getValue(true);
            if (!Array.isArray(currentValue)) {
                this.handleSelection(currentValue, null);
            }
        });

        if (shouldSearchRemotely) {
            this.bindAsyncSearch();
        }
    },
    captureInitialOptions(providedOptions = []) {
        const optionElements = Array.from(this.$refs.select.options ?? []);

        optionElements.forEach((element) => {
            if (!element.value) {
                return;
            }

            const normalized = this.normalizeOptionFromElement(element);
            if (normalized) {
                this.optionsByValue.set(normalized.value, normalized.option);
            }
        });

        providedOptions.forEach((raw) => {
            const normalized = this.normalizeOption(raw);
            if (normalized) {
                this.optionsByValue.set(normalized.value, normalized.option);
            }
        });
    },
    normalizeOptionFromElement(element) {
        const raw = element.dataset.option ?? null;

        if (raw) {
            try {
                const parsed = JSON.parse(raw);
                return this.normalizeOption(
                    parsed,
                    element.textContent ?? undefined,
                );
            } catch (error) {
                console.warn("Unable to parse picker option payload", error);
            }
        }

        return this.normalizeOption({
            id: element.value,
            value: element.value,
            label: element.textContent ?? element.value,
        });
    },
    normalizeOption(option, fallbackLabel) {
        if (!option) {
            return null;
        }

        const rawValue = option.id ?? option.value;

        if (rawValue === undefined || rawValue === null || rawValue === "") {
            return null;
        }

        const value = String(rawValue);
        const label = this.resolveOptionLabel(option, fallbackLabel);

        return {
            value,
            label,
            option: {
                ...option,
                id: option.id ?? value,
                value,
                label,
            },
        };
    },
    resolveOptionLabel(option, fallback) {
        const labelKey = this.optionLabelKey ?? "label";
        const candidate =
            option[labelKey] ??
            option.label ??
            option.name ??
            fallback ??
            option.value ??
            option.id;
        return candidate !== undefined && candidate !== null
            ? String(candidate)
            : "";
    },
    bindAsyncSearch() {
        const element = this.instance.passedElement.element;

        element.addEventListener("search", (event) => {
            const term = String(event.detail?.value ?? "");
            this.queueAsyncSearch(term);
        });
    },
    queueAsyncSearch(term) {
        const delay = Number(this.asyncConfig?.debounce ?? 250);

        if (this.searchDebounceTimer !== null) {
            clearTimeout(this.searchDebounceTimer);
        }

        this.searchDebounceTimer = window.setTimeout(
            () => {
                void this.performAsyncSearch(term);
            },
            Number.isFinite(delay) ? Math.max(delay, 0) : 250,
        );
    },
    async performAsyncSearch(term) {
        const minimumCharacters = Number(
            this.asyncConfig?.minimumCharacters ?? 2,
        );

        if (!term || term.length < minimumCharacters) {
            return;
        }

        if (this.searchAbortController) {
            this.searchAbortController.abort();
        }

        this.searchAbortController = new AbortController();

        const method = (this.asyncConfig?.method ?? "GET").toUpperCase();
        const queryParam = this.asyncConfig?.queryParam ?? "q";
        const url = this.buildSearchUrl(term, method, queryParam);
        const requestInit = this.buildSearchRequestInit(
            method,
            term,
            queryParam,
        );

        requestInit.signal = this.searchAbortController.signal;

        try {
            const response = await fetch(url, requestInit);

            if (!response.ok) {
                throw new Error(
                    `Picker search failed with status ${response.status}`,
                );
            }

            const payload = await response.json();
            const rawOptions = Array.isArray(payload?.data)
                ? payload.data
                : Array.isArray(payload)
                  ? payload
                  : [];

            const normalized = rawOptions
                .map((option) => this.normalizeOption(option))
                .filter(Boolean);

            this.replaceChoices(normalized);
        } catch (error) {
            if (error?.name === "AbortError") {
                return;
            }

            console.error(error);
        }
    },
    buildSearchUrl(term, method, queryParam) {
        let url = this.asyncConfig?.endpoint ?? "";

        if (method === "GET") {
            const params = new URLSearchParams();
            params.set(queryParam, term);

            if (url.includes("?")) {
                url += `&${params.toString()}`;
            } else {
                url += `?${params.toString()}`;
            }
        }

        return url;
    },
    buildSearchRequestInit(method, term, queryParam) {
        const headers = {
            Accept: "application/json",
        };

        if (method === "GET") {
            return {
                method,
                headers,
            };
        }

        headers["Content-Type"] = "application/json";

        return {
            method,
            headers,
            body: JSON.stringify({
                [queryParam]: term,
            }),
        };
    },
    replaceChoices(normalizedOptions) {
        const currentValue = this.instance.getValue(true);
        const selectedValue = Array.isArray(currentValue)
            ? currentValue[0]
            : currentValue;

        const choices = normalizedOptions.map(({ value, label, option }) => {
            this.optionsByValue.set(value, option);

            return {
                value,
                label,
                selected:
                    selectedValue !== undefined &&
                    selectedValue !== null &&
                    String(selectedValue) === value,
                disabled: false,
                customProperties: {
                    option,
                },
            };
        });

        this.instance.setChoices(choices, "value", "label", true);

        if (
            selectedValue !== undefined &&
            selectedValue !== null &&
            selectedValue !== ""
        ) {
            this.instance.setChoiceByValue(String(selectedValue));
        }
    },
    handleSelection(value, optionData) {
        if (value === undefined || value === null) {
            if (model && this.$wire) {
                this.$wire.set(model, null);
            }

            return;
        }

        const resolvedValue = String(value);
        const normalizedOption =
            optionData ?? this.optionsByValue.get(resolvedValue) ?? null;

        if (normalizedOption) {
            this.optionsByValue.set(resolvedValue, normalizedOption);
        }

        this.value = resolvedValue;

        if (model && this.$wire) {
            this.$wire.set(model, resolvedValue);

            if (
                this.onOptionSelect &&
                typeof this.$wire[this.onOptionSelect] === "function"
            ) {
                this.$wire[this.onOptionSelect](
                    resolvedValue,
                    normalizedOption,
                );
            }
        }
    },
});

export const registerAsyncPicker = (Alpine) => {
    Alpine.data("customerPicker", (config) => buildAsyncPicker(config));
    Alpine.data("asyncPicker", (config) => buildAsyncPicker(config));
};
